/**
 * src/api/chat.ts
 * ----------------
 * API functions for the Helix chat messaging endpoints.
 *
 * Fully implemented this will:
 *   - sendMessage(sessionId, content, attachments?) — POST /sessions/{id}/messages
 *     Returns a server-sent event stream URL or the assistant's full reply.
 *   - streamMessage(sessionId, content) — opens an EventSource to
 *     /sessions/{id}/stream, emitting typed StreamEvent objects
 *     (token, tool_call, citation, action, done, error).
 *   - getMessages(sessionId) — GET /sessions/{id}/messages (paginated)
 *   - deleteMessage(sessionId, messageId) — DELETE for message retraction
 *   - reactToMessage(sessionId, messageId, reaction) — POST for thumbs up/down
 *
 * Key dependencies:
 *   src/api/client.ts, src/types/message.ts
 */
import apiClient from './client'
import { getCurrentOrgId } from '@/lib/org'
import { useAuthStore } from '@/stores/auth'
import type { Message, StreamEvent, Reaction } from '@/types/message'

type MessageResponse = Message & {
  session_id?: string
  created_at?: string
  tool_calls?: Message['toolCalls']
  token_count?: number
}

function normalizeMessage(message: MessageResponse): Message {
  return {
    ...message,
    sessionId: message.sessionId ?? message.session_id ?? '',
    createdAt: message.createdAt ?? message.created_at ?? new Date().toISOString(),
    toolCalls: message.toolCalls ?? message.tool_calls,
    tokenCount: message.tokenCount ?? message.token_count,
  }
}

/** Send a user message and receive the assistant's full response (non-streaming). */
export async function sendMessage(
  sessionId: string,
  content: string,
  attachments: File[] = [],
): Promise<Message> {
  if (attachments.length > 0) {
    throw new Error('Attachments are not supported by this endpoint yet.')
  }
  const { data } = await apiClient.post<{ message: MessageResponse }>(
    `/sessions/${sessionId}/messages`,
    { content },
    { timeout: 90_000 }, // Grounded generation may include one bounded repair.
  )
  return normalizeMessage(data.message)
}

/** Open an SSE stream for a streaming assistant response. */
export function streamMessage(
  sessionId: string,
  content: string,
  onEvent: (event: StreamEvent) => void,
  onError?: (err: Event) => void,
): EventSource {
  const token = useAuthStore.getState().accessToken ?? ''
  const url = new URL(
    `/api/v1/sessions/${sessionId}/stream`,
    window.location.origin,
  )
  url.searchParams.set('content', content)
  url.searchParams.set('org_id', getCurrentOrgId())
  if (token) url.searchParams.set('token', token)

  const es = new EventSource(url.toString())
  es.onmessage = (e) => {
    if (e.data === '[DONE]') {
      es.close()
      return
    }
    const event: StreamEvent = JSON.parse(e.data as string)
    if (event.message) event.message = normalizeMessage(event.message as MessageResponse)
    onEvent(event)
    if (event.type === 'done' || event.type === 'error') es.close()
  }
  es.onerror = (event) => { es.close(); onError?.(event) }
  return es
}

/** Fetch message history for a session (page-based). */
export async function getMessages(
  sessionId: string,
  page = 1,
  pageSize = 50,
): Promise<{ messages: Message[]; total: number; status?: string; request_feedback?: boolean; human_takeover?: boolean }> {
  const { data } = await apiClient.get<{
    messages: MessageResponse[]
    total: number
    status?: string
    request_feedback?: boolean
    human_takeover?: boolean
  }>(`/sessions/${sessionId}/messages`, {
    params: { page, page_size: pageSize },
  })
  return { ...data, messages: data.messages.map(normalizeMessage) }
}

/** Submit a thumbs-up or thumbs-down reaction to an assistant message. */
export async function reactToMessage(
  sessionId: string,
  messageId: string,
  reaction: Reaction,
): Promise<void> {
  await apiClient.post(`/sessions/${sessionId}/messages/${messageId}/reaction`, null, {
    params: { reaction },
  })
}

/** Delete (retract) a user-sent message. */
export async function deleteMessage(
  sessionId: string,
  messageId: string,
): Promise<void> {
  await apiClient.delete(`/sessions/${sessionId}/messages/${messageId}`)
}
